import {
  DESKTOP_EXPANDED_PLAYER_INSET_CLASS,
  MINI_PLAYER_POSITION_CLASS,
  MINI_PLAYER_SIZE_CLASS,
  MOBILE_VIDEO_FRAME_CLASS,
  MOBILE_VIDEO_FRAME_POSITION_CLASS,
} from '@app/web-player/player/mobile-video-frame';
import {
  markResumePlaybackAfterOverlayOpen,
  registerPlayerGetState,
  scheduleResumePlaybackIfPending,
} from '@app/web-player/player/resume-playback-after-overlay';
import {usePlayerOverlayStore} from '@app/web-player/state/player-overlay-store';
import {PlayerStoreContext} from '@common/player/player-context';
import {usePlayerStore} from '@common/player/hooks/use-player-store';
import {PlayerOutlet} from '@common/player/ui/player-outlet';
import {useMediaQuery} from '@ui/utils/hooks/use-media-query';
import clsx from 'clsx';
import {useContext, useLayoutEffect, useRef} from 'react';

/**
 * Single stable mount for YouTube / HTML5 media. The provider DOM must never be
 * reparented (appendChild) or playback pauses/restarts — only CSS classes change.
 */
export function PersistentPlayerMount() {
  const store = useContext(PlayerStoreContext);
  const hostRef = useRef<HTMLDivElement>(null);
  const wasMaximizedRef = useRef(false);
  const isMaximized = usePlayerOverlayStore(s => s.isMaximized);
  const isFullscreen = usePlayerStore(s => s.isFullscreen);
  const providerName = usePlayerStore(s => s.providerName);
  const mediaIsCued = usePlayerStore(s => s.cuedMedia != null);
  const isDesktop = useMediaQuery('(min-width: 1025px)');

  const haveVideo = providerName != null && providerName !== 'htmlAudio';
  const showExpandedVideo = mediaIsCued && haveVideo && isMaximized;
  const showDesktopMini =
    isDesktop && mediaIsCued && haveVideo && !isMaximized && !isFullscreen;

  useLayoutEffect(() => {
    registerPlayerGetState(store.getState);
  }, [store]);

  useLayoutEffect(() => {
    const openedOverlay = isMaximized && !wasMaximizedRef.current;
    const closedOverlay = !isMaximized && wasMaximizedRef.current;
    wasMaximizedRef.current = isMaximized;

    if (openedOverlay || closedOverlay) {
      markResumePlaybackAfterOverlayOpen();
      scheduleResumePlaybackIfPending(store.getState);
    }
  }, [isMaximized, store]);

  if (!mediaIsCued) {
    return null;
  }

  const anchorClassName = clsx(
    'fullscreen-host fixed isolate overflow-hidden',
    showExpandedVideo
      ? isFullscreen
        ? 'inset-0 z-[35] bg-black'
        : isDesktop
          ? clsx('z-[43] bg-black', DESKTOP_EXPANDED_PLAYER_INSET_CLASS)
          : clsx(
              'z-[43] bg-black',
              MOBILE_VIDEO_FRAME_CLASS,
              MOBILE_VIDEO_FRAME_POSITION_CLASS,
            )
      : showDesktopMini
        ? clsx(
            MINI_PLAYER_POSITION_CLASS,
            MINI_PLAYER_SIZE_CLASS,
            'z-20 bg-black',
          )
        : 'pointer-events-none bottom-0 left-0 z-0 h-36 w-64 bg-black opacity-[0.01]',
  );

  return (
    <div
      className={anchorClassName}
      aria-hidden={!showExpandedVideo && !showDesktopMini}
    >
      <div ref={hostRef} className="h-full w-full bg-black">
        <PlayerOutlet className="h-full w-full" />
      </div>
    </div>
  );
}
